import React, { ComponentProps } from 'react';
import { Anchor as DLSAnchor } from '@americanexpress/dls-react';

import Typography from 'src/components/typography/Typography';
import { TypographyViewModel } from 'src/types/view-models/typography.view.model';
import { useLocation } from 'react-router';
import { trackClick, trackClickWithoutPath } from 'src/hooks/useAdobeTags';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import { isMobileApp } from 'src/utils/url.utils';

interface Props extends ComponentProps<'a'> {
    onClick?: () => Promise<void> | void;
    tag?: string;
    theme?: Record<string, any>;
    children: React.ReactNode;
    typographyProps?: TypographyViewModel;
    inline?: boolean;
}

/**
 * Three scenarios for use:
 * 1. Navigate using default `<a>` tag behaviour (with optional tracking)
 * -> Pass in a `href` and not an `onClick`
 * 2. Navigate using React Router (with optional tracking)
 * -> Pass in both a `href` and a navigation `onClick`
 * 3. Don't navigate, instead run only side effects (e.g. opening modal)
 * -> Pass in a side effect `onClick` and not a `href`
 *
 * See README.md for further details
 */
export default function Anchor({ onClick, children, typographyProps, href = '', tag, ...props }: Props): JSX.Element {
    const { pathname } = useLocation();
    const trackClickWithoutPathPages: string[] = [BudgetPages.INELIGIBLE];

    const dlsAnchor = (
        <DLSAnchor
            onClick={async (e: KeyboardEvent): Promise<void> => {
                if (tag)
                    trackClickWithoutPathPages.includes(pathname)
                        ? trackClickWithoutPath(tag)
                        : trackClick(pathname, tag);

                // Scenario 3
                // When href doesn't exist we are not navigating the user so prevent default and call onClick
                if (!href) {
                    e.preventDefault();
                    await onClick?.();
                }
                // Scenario 2
                // When href and onClick exist we try to navigate using the onClick method unless Ctrl/Cmd is pressed
                if (href && !e.metaKey && !e.ctrlKey && onClick) {
                    e.preventDefault();
                    await onClick();
                }
                // Scenario 1
                // When onClick doesn't exist or if Ctrl/Cmd are pressed then let the default behaviour happen
            }}
            href={href}
            role={href ? 'link' : 'button'}
            target={onClick || isMobileApp() ? '_self' : '_blank'}
            rel="noopener noreferrer"
            {...props}
        >
            {children}
        </DLSAnchor>
    );

    return <>{typographyProps ? <Typography {...typographyProps}>{dlsAnchor}</Typography> : dlsAnchor}</>;
}
